import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Send, Share2 } from 'lucide-react';
import MenuShareLinkDialog from '@/components/menu/MenuShareLinkDialog';
import type { Menu } from '@/hooks/useMultiMenu';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { canPrepareMenuShare, prepareMenuShareUrl } from '@/lib/chatMenuShare';
import { composeRoute } from '@/lib/entityLinks';
import { shareReadyLine, shareTemplates, menuShareMessage } from '@/lib/menuShareReady';

export const FEATURE_META = {
  id: 'chat-menu-share', label: 'Share a menu from chat',
  description: 'Prepares an owned menu link and a buyer message for review before sending.',
  triggerType: 'action' as const, personas: ['seller', 'buyer_seller'] as ('seller' | 'buyer_seller')[],
};

export function ShareMenuAction({ menuId, companyId, children }: { menuId: string; companyId: string; children: ReactNode }) {
  useFeatureRegistration(FEATURE_META);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const prepare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: auth, error: authError } = await supabase.auth.getUser();
      if (authError || !auth.user) throw new Error('No session');
      const { data: owner, error: ownerError } = await supabase.from('users').select('company_id').eq('id', auth.user.id).single();
      if (ownerError || !owner?.company_id || owner.company_id === companyId) throw new Error('No company');
      const { data: menu, error: menuError } = await supabase.from('menus').select('id,name,company_id,is_active,archived_at').eq('id', menuId).eq('company_id', owner.company_id).single();
      if (menuError || !menu) throw new Error('No menu');
      const { count, error: countError } = await supabase.from('menu_listings')
        .select('listing_id,listings!inner(id)', { count: 'exact', head: true })
        .eq('menu_id', menuId).not('listings.status', 'in', '(draft,archived)')
        .eq('listings.is_archived', false);
      if (countError) throw countError;
      if (!canPrepareMenuShare(menu, owner.company_id, count ?? 0)) {
        toast.info('This menu is not ready to share', { description: !menu.is_active || menu.archived_at ? 'Choose an active menu.' : shareReadyLine(count ?? 0) ?? undefined });
        return;
      }
      // Live relationship check before creating a link; never trust model-provided recipients.
      const { data: contacts, error: contactsError } = await supabase.rpc('get_counterparty_contacts' as never, { p_company_id: companyId } as never);
      const people: unknown = contacts;
      if (contactsError || !Array.isArray(people) || people.length === 0) throw new Error('Recipient unavailable');
      const url = await prepareMenuShareUrl(menuId, owner.company_id);
      navigate(composeRoute(companyId, menuShareMessage(shareTemplates(menu.name, count ?? 0)[0].text, url)));
    } catch {
      toast.error('Could not prepare this menu share', { description: 'Choose an active menu you own and a buyer connected to your company, then try again.' });
    } finally { setBusy(false); }
  };
  return <Button size="sm" className="my-1 h-auto min-h-8 gap-1.5 whitespace-normal text-left" disabled={busy} onClick={() => void prepare()}>
    {busy ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden /> : <Send className="h-3.5 w-3.5 shrink-0" aria-hidden />}{children}
  </Button>;
}
/** No buyer named yet: open the menu's own Share window in the chat, so the user shares and keeps chatting. */
export function OpenMenuShareAction({ menuId, children }: { menuId: string; children: ReactNode }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<{ menu: Menu; companyId: string } | null>(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    if (busy) return;
    if (state) { setOpen(true); return; }
    setBusy(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) throw new Error('No session');
      const { data: owner } = await supabase.from('users').select('company_id').eq('id', auth.user.id).single();
      if (!owner?.company_id) throw new Error('No company');
      const { data: menu, error } = await supabase.from('menus').select('*').eq('id', menuId).eq('company_id', owner.company_id).single();
      if (error || !menu || !menu.is_active || menu.archived_at) throw new Error('No menu');
      setState({ menu: menu as unknown as Menu, companyId: owner.company_id });
      setOpen(true);
    } catch {
      toast.error('Could not open sharing for this menu', { description: 'Choose an active menu your company owns.' });
    } finally { setBusy(false); }
  };
  return <>
    <Button size="sm" variant="outline" className="mx-1 my-0.5 h-7 gap-1.5 px-2 text-xs" disabled={busy} onClick={() => void load()}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Share2 className="h-3 w-3" aria-hidden />}{children}
    </Button>
    {state && <MenuShareLinkDialog open={open} onOpenChange={setOpen} menu={state.menu} companyId={state.companyId} />}
  </>;
}
